import { useSuperchat } from '../store.js';
import { Launcher } from './Launcher.jsx';
import { Header } from './Header.jsx';
import { PreChat } from './PreChat.jsx';
import { Offline } from './Offline.jsx';
import { Thread } from './Thread.jsx';
import { Composer } from './Composer.jsx';
import { TestTools } from './TestTools.jsx';
import { EmailCapture } from './EmailCapture.jsx';
import { BoltIcon } from './Icons.jsx';

function Footer() {
  return (
    <div class="footer">
      <BoltIcon size={10} />
      Powered by Superchat
    </div>
  );
}

export function App({ config, controller, onStateChange }) {
  const sc = useSuperchat(config);
  const { actions } = sc;

  // Back the imperative window.SuperChat API with the same actions the UI uses.
  if (controller) {
    controller.open = actions.open;
    controller.close = actions.close;
    controller.send = actions.send;
    controller.setTopUrl = actions.setTopUrl;
  }

  // Let the embedding page react (the launcher's own position, analytics).
  onStateChange?.({ open: sc.open, unread: sc.unread, phase: sc.phase });

  if (sc.phase === 'failed') {
    // A widget that cannot authenticate must not litter the merchant's admin
    // with a broken panel — it just doesn't appear.
    if (config.debug) console.error('[superchat] failed to start', sc.error);
    return null;
  }

  const initial = (sc.brand || 'S').trim().charAt(0).toUpperCase();
  /**
   * An agent sent a user alert, which forced this panel open.
   *
   * Nothing about that is shown to the visitor any more: no banner, no tagged
   * bubble, no altered header or launcher. The alert is a thing agents can see
   * about their own conversations — from where the visitor sits, an ordinary
   * reply arrived and the chat happened to be open for it.
   *
   * The flag survives only to route the panel to the thread rather than the
   * pre-chat form, since the point of forcing it open is that the message is
   * on screen when it gets there.
   */
  const forcedOpen = Boolean(sc.emergency);

  // White-label: the header fills with the accent instead of the panel colour.
  const headerVars = config.solidHeader
    ? {
        '--sc-header-bg': sc.accent,
        '--sc-header-line': 'transparent',
        '--sc-header-tx': '#fff',
        '--sc-header-tx2': 'rgba(255,255,255,.75)',
        '--sc-mark-bg': 'rgba(255,255,255,.22)',
      }
    : {
        '--sc-header-bg': 'var(--sc-panel)',
        '--sc-header-line': 'var(--sc-line)',
        '--sc-header-tx': 'var(--sc-tx)',
        '--sc-header-tx2': 'var(--sc-tx3)',
        '--sc-mark-bg': sc.accent,
      };

  const startConversation = ({ topic, body, shop }) => {
    // Topic and store go with the first message rather than into a separate
    // call — one round trip, and the agent sees the routing context inline.
    const prefix = [topic && `[${topic}]`, shop && `(${shop})`].filter(Boolean).join(' ');
    actions.send({ body: prefix ? `${prefix}\n${body}` : body });
  };

  const showThread = sc.view === 'chat' || forcedOpen;

  return (
    /* The side is carried by a class on the host element rather than by this
       attribute, so it can be changed after boot; this only reports it. */
    <div class="root" data-position={config.position} style={{ '--sc-accent': sc.accent }}>
      {sc.open && (
        <div class="panel" style={headerVars} role="dialog" aria-label={`${sc.brand} chat`}>
          <Header
            brand={sc.brand}
            initial={initial}
            connection={sc.connection}
            offline={sc.offline}
            onBack={sc.view === 'doc' ? () => actions.setView('prechat') : null}
            onMinimize={actions.close}
            onClose={actions.close}
          />

          {/* A broadcast test that arrived from an agent. Transient. */}
          {sc.diagnostic && <div class="diagnostic-bar">{sc.diagnostic}</div>}

          {sc.phase === 'booting' && (
            <div class="form">
              <div class="form__sub">Connecting to support…</div>
            </div>
          )}

          {sc.phase === 'ready' && sc.view === 'doc' && sc.activeDoc && (
            <div class="doc-body">
              <h3>{sc.activeDoc.title}</h3>
              {sc.activeDoc.body}
            </div>
          )}

          {sc.phase === 'ready' && sc.view === 'prechat' && !forcedOpen && (
            <PreChat
              welcome={sc.welcome}
              shop={config.shop}
              docs={sc.docs}
              sending={sc.sending}
              onStart={startConversation}
              onOpenDoc={actions.openDoc}
            />
          )}

          {sc.phase === 'ready' && sc.view === 'offline' && !forcedOpen && (
            <Offline nextOpen={sc.nextOpen} sending={sc.sending} onSend={actions.send} />
          )}

          {sc.phase === 'ready' && showThread && (
            <>
              {/* Nothing to ask for if the snippet already told us who this
                  is — the common case in an embedded merchant admin. */}
              {!sc.conversation?.visitor?.email && (
                <EmailCapture
                  collapsed={sc.emailPromptDismissed}
                  onSave={actions.saveEmail}
                  onDismiss={actions.dismissEmailPrompt}
                  onExpand={actions.reopenEmailPrompt}
                />
              )}
              <Thread
                messages={sc.messages}
                agentTyping={sc.agentTyping}
                hasMore={sc.hasMore}
                loadingMore={sc.loadingMore}
                onLoadMore={actions.loadMore}
                onRetry={actions.retry}
                onResolveAttachment={actions.attachmentUrl}
              />
              <Composer
                onSend={actions.send}
                onTyping={actions.setTyping}
                sending={sc.sending}
                onUpload={actions.upload}
                uploads={sc.uploads}
              />
            </>
          )}

          {(config.testTools || config.debug) && sc.phase === 'ready' && (
            <TestTools onNotifyAgents={actions.notifyAgents} onTestTone={actions.testTone} />
          )}

          {config.showBadge && <Footer />}
        </div>
      )}

      {!sc.open && (
        <Launcher brand={sc.brand} unread={sc.unread} onOpen={actions.open} />
      )}
    </div>
  );
}

export default App;
